import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import PageTransition from "@/components/PageTransition";
import { getReferral, getWallet, fmtNum } from "@/lib/wallet";
import { Copy, Check, Share2, Gift, Users, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
  const [ref, setRef] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setWallet(getWallet());
    setRef(getReferral());
  }, []);

  const link = ref ? `${window.location.origin}/?ref=${ref.code}` : "";

  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const totalEarned = ref?.referrals?.reduce((a, r) => a + r.earned, 0) || 0;
  const totalStaked = ref?.referrals?.reduce((a, r) => a + r.totalStaked, 0) || 0;

  return (
    <PageTransition>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-20">
        <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "var(--text-3)" }}>▍ Refer & earn</div>
        <h1 className="font-display font-black text-5xl tracking-tighter">Referrals</h1>
        <p className="mt-3 text-sm max-w-xl" style={{ color: "var(--text-2)" }}>
          Invite friends — earn 10% of their staking rewards forever. Paid out automatically every 24h.
        </p>

        {!wallet ? (
          <div className="border mt-10 p-12 text-center" style={{ borderColor: "var(--border)", background: "var(--surface-1)" }}>
            <div className="text-sm" style={{ color: "var(--text-2)" }}>Connect a wallet to claim your referral code.</div>
          </div>
        ) : !ref ? null : (
          <>
            <div className="grid md:grid-cols-3 gap-px mt-10" style={{ background: "var(--border)" }}>
              <Tile icon={<Users        size={16} />} label="Active referrals" value={ref.referrals.length} />
              <Tile icon={<TrendingUp   size={16} />} label="Volume routed"   value={`$${fmtNum(totalStaked, 0)}`} />
              <Tile icon={<Gift         size={16} />} label="You earned"      value={`$${fmtNum(totalEarned, 0)}`} accent="var(--success)" />
            </div>

            <div className="border p-6 mt-6" style={{ borderColor: "var(--border)", background: "var(--surface-1)" }}>
              <div className="text-[10px] uppercase tracking-widest mb-3" style={{ color: "var(--text-3)" }}>Your unique link</div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[260px] font-mono text-sm break-all border px-4 py-3" style={{ borderColor: "var(--border)" }}>{link}</div>
                <button
                  onClick={copy}
                  data-testid="copy-referral-btn"
                  className="px-5 h-11 inline-flex items-center gap-2 font-bold tracking-wide"
                  style={{ background: "#fff", color: "#000" }}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? "Copied" : "Copy link"}
                </button>
                <button
                  onClick={() => {
                    if (navigator.share) navigator.share({ title: "Stakeforge", url: link });
                    else copy();
                  }}
                  data-testid="share-referral-btn"
                  className="px-5 h-11 inline-flex items-center gap-2 font-bold tracking-wide border"
                  style={{ borderColor: "var(--border-strong)" }}
                >
                  <Share2 size={14} /> Share
                </button>
              </div>
              <div className="mt-4 text-[11px]" style={{ color: "var(--text-3)" }}>
                Referral code · <span className="font-mono text-white">{ref.code}</span>
              </div>
            </div>

            <div className="mt-10">
              <div className="text-[11px] uppercase tracking-[0.25em] mb-4" style={{ color: "var(--text-3)" }}>▍ Your downline</div>
              <div className="border" style={{ borderColor: "var(--border)" }}>
                <div className="grid grid-cols-12 px-6 py-3 text-[10px] uppercase tracking-widest border-b" style={{ borderColor: "var(--border)", color: "var(--text-3)" }}>
                  <div className="col-span-4">Handle</div>
                  <div className="col-span-3">Joined</div>
                  <div className="col-span-3 text-right">Total staked</div>
                  <div className="col-span-2 text-right">You earned</div>
                </div>
                {ref.referrals.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 px-6 py-4 border-b items-center text-sm" style={{ borderColor: "var(--border)" }}>
                    <div className="col-span-4 font-mono">@{r.handle}</div>
                    <div className="col-span-3 text-xs" style={{ color: "var(--text-3)" }}>{new Date(r.joinedAt).toLocaleDateString()}</div>
                    <div className="col-span-3 text-right font-mono">${fmtNum(r.totalStaked, 0)}</div>
                    <div className="col-span-2 text-right font-mono" style={{ color: "var(--success)" }}>+${fmtNum(r.earned, 0)}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </PageTransition>
  );
}

function Tile({ icon, label, value, accent }) {
  return (
    <div className="p-6" style={{ background: "var(--surface-1)" }}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>{label}</div>
        <div style={{ color: "var(--text-3)" }}>{icon}</div>
      </div>
      <div className="font-mono font-bold text-3xl mt-3" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}
